import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CONFIG } from 'config/config';
import { professionsList } from 'const/professions';
import { provincesList } from 'const/provinces';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
	NavigationWrapper,
	TargetsWrapper,
	Text,
	UiWrapper,
	UserWrapper,
	View,
} from '@coinscrap/webapp-core';
import { Wrapper } from 'layout/components/Body/Wrapper';
import Footer from 'layout/components/Footer/Footer';
import Header from 'layout/components/Header/Header';
import BackNextButton from 'components/BackNextButton/BackNextButton';
import { Loader } from 'components/Loader/Loader';
import { MainModal } from 'components/MainModal/MainModal';
import { useGetError } from 'hooks/useGetError';
import { useInitialScrollToTop } from 'hooks/useInitialScrollToTop';
import { ValidateSpanishID } from 'helpers/dni';
import { DebugContext } from '../../App';
import InputLabel from '../../components/InputLabel/InputLabel';
import { OptionButton } from '../../components/OptionButton/OptionButton';
import { AgentContext } from '../../contexts/agent';
import { useCoinscrapPageView } from '../../hooks/useCoinscrapPageView';
import { ClientSimulationWrapper } from '../../wrappers/customWrapper/ClientSimulationWrapper';
import { BirthDate } from './components/BirthDate';
import { Document } from './components/Document';
import { PostalCode } from './components/PostalCode';
import { Profession } from './components/Profession';
import { ProfessionQuestions } from './components/ProfessionQuestions';
import { useProfession } from './hooks/useProfession';
import { useProfessionError } from './hooks/useProfessionError';
import { useTowns } from './hooks/useTowns';

export const SimulationDataBase = ({
	internalStep,
	targetType,
	target,
	user,
	institution,
	isB2B,
}) => {
	const { agent } = useContext(AgentContext);
	const { saveTargetProperty } = TargetsWrapper.use();
	const { navigateTo } = NavigationWrapper.use();
	const { updateUser } = UserWrapper.use();
	const { updateClient, updateSimulation, loadSimulationForClient } =
		ClientSimulationWrapper?.use() || {};
	const { useHeader, useFooter, useLoading, openModal } = UiWrapper.use();
	const { t, i18n } = useTranslation();

	const isDebug = React.useContext(DebugContext);

	const [loading, setLoading] = useState();

	useFooter(<Footer />, []);
	useHeader(<Header />, []);
	useLoading(loading, { loadingMessage: loading });
	useInitialScrollToTop();
	useCoinscrapPageView('SimulationData');
	const getError = useGetError();

	// TARGET CREATION DATA
	const creationData = useMemo(
		() => target?.targetProperties?.find((x) => x.name === 'creationData')?.value,
		[target],
	);

	// ESTADO
	const [sectionTitle, setSectionTitle] = useState('');
	const [sectionSubtitle, setSectionSubtitle] = useState('');
	const [simulationDataStep, setSimulationDataStep] = useState(false);
	useEffect(() => {
		if (internalStep) {
			setSimulationDataStep(internalStep);
		} else {
			setSimulationDataStep(1);
		}
	}, [internalStep]);

	const [dni, setDni] = useState(user?.metadata?.document || '');
	const [isValidDNI, setIsValidDNI] = useState(true);
	const [invalidDNIreason, setInvalidDNIreason] = useState(
		'El número de documento introducido no es válido',
	);

	const [birthDate, setBirthDate] = useState(user?.metadata?.birthDate || null);

	const [postalCode, setPostalCode] = useState(user?.metadata?.address?.postalCode || '');
	const [isPostalCorrect, setIsPostalCorrect] = useState(true);

	const [municipalitiesList, setMunicipalitiesList] = useState([]);
	const [municipality, setMunicipality] = useState(user?.metadata?.address?.municipality || '');

	const [province, setProvince] = useState(user?.metadata?.address?.province || '');
	const [country, setCountry] = useState(user?.metadata?.address?.country || '');

	const [profession, setProfession] = useState(user?.metadata?.profession || '');
	const [professionCode, setProfessionCode] = useState(user?.metadata?.professionCode || '');

	const [professionResponses, setProfessionResponses] = useState(
		(user?.metadata?.professionQuestions || []).reduce(
			(acc, question) => ({
				...acc,
				[question.id]: question?.response,
			}),
			{},
		),
	);

	const [trigger, setTrigger] = useState(0);

	const { loadingQuestions, professionQuestions, professionWarning } = useProfession(
		simulationDataStep,
		user?.metadata?.professionQuestions,
		target.id,
		professionCode,
		trigger,
		setTrigger,
		useProfessionError,
		agent,
	);

	const { loadingTowns, towns, errorTowns } = useTowns(
		target.id,
		postalCode,
		trigger,
		setTrigger,
	);

	// NAVIGATION
	const next = async () => {
		switch (simulationDataStep) {
			case 1:
				try {
					const currentAge = moment().diff(moment(birthDate), 'years');

					setLoading('Guardando...');
					const userPayload = {
						metadata: {
							...user?.metadata,
							document: dni,
							documentType: ValidateSpanishID(dni).type,
							birthDate,
							currentAge,
							address: {
								...user?.metadata?.address,
								postalCode,
								municipality,
								province,
								country,
							},
							isB2B,
						},
					};

					if (isB2B) {
						await updateClient(userPayload);
					} else {
						await updateUser(userPayload);
					}
					setLoading('Actualizando...');
					const targetPayload = {
						...creationData,
						details: {
							...creationData?.details,
							isB2B,
						},
					};

					if (isB2B) {
						await updateSimulation(targetPayload);
					} else {
						await saveTargetProperty(target.id, 'creationData', targetPayload);
					}

					setSimulationDataStep(2);
					setLoading(undefined);
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-SimulationData-Step1-KO',
					});
				}
				break;
			case 2:
				try {
					setLoading('Guardando...');
					const userPayload = {
						metadata: {
							...user?.metadata,
							profession,
							professionCode: professionsList?.find((p) => p?.NAME === profession)
								?.VALUE,
							professionQuestions: professionQuestions.map((question) => {
								return {
									...question,
									response: professionResponses[question.id],
								};
							}),
						},
					};

					if (isB2B) {
						await updateClient(userPayload);
					} else {
						await updateUser(userPayload);
					}
					setLoading('Actualizando...');
					const targetPayload = {
						...creationData,
						details: {
							...creationData?.details,
							isB2B,
						},
					};

					if (isB2B) {
						await updateSimulation(targetPayload);
					} else {
						await saveTargetProperty(target.id, 'creationData', targetPayload);
					}
					if (professionQuestions.some((qu) => professionResponses?.[qu.id] === true)) {
						CONFIG.routing.simulationData.ko(navigateTo);
					} else {
						CONFIG.routing.simulationData.next(navigateTo);
					}
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-SimulationData-Step1-KO',
					});
				}
				break;
			default:
				setLoading(undefined);
				openModal(
					() => (
						<MainModal
							type="error"
							text={getError(
								new Error(
									`simulationDataStep no válido: simulationDataStep ${simulationDataStep}`,
								),
							)}
						/>
					),
					{
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-SimulationData_defaultKO',
					},
				);
				break;
		}
	};

	const isDisabled = () => {
		switch (simulationDataStep) {
			case 1:
				return (
					!dni ||
					!isValidDNI ||
					dni.length < 9 ||
					!birthDate ||
					moment(birthDate).unix() > moment().subtract(18, 'years').unix() ||
					moment(birthDate).unix() < moment().subtract(67, 'years').unix() ||
					!postalCode ||
					postalCode.length !== 5 ||
					!municipality
				);
			case 2:
				return (
					!profession ||
					!professionCode ||
					loadingQuestions ||
					(professionQuestions.length > 0 &&
						professionQuestions.some(
							(qu) => professionResponses?.[qu.id] === undefined,
						) &&
						!professionQuestions.some((qu) => professionResponses?.[qu.id] === true))
				);
			default:
				return true;
		}
	};

	useEffect(() => {
		switch (simulationDataStep) {
			case 1:
				setSectionTitle('Datos del asegurado');
				setSectionSubtitle('Necesitamos tus datos para calcular el precio de tu seguro.');
				break;
			case 2:
				setSectionTitle('Datos del asegurado');
				setSectionSubtitle('Necesitamos tus datos para calcular el precio de tu seguro.');
				break;
			default:
				setSectionTitle('Datos iniciales');
				break;
		}
		return () => {};
	}, [simulationDataStep]);

	useEffect(() => {
		if (towns) {
			setMunicipalitiesList(towns.map((town) => ({ label: town.town, value: town.town })));
			if (towns?.length === 1) {
				setMunicipality(towns[0]?.town);
			}
		}
		return () => {};
	}, [towns, user]);

	useEffect(() => {
		if (professionQuestions?.length) {
			setProfessionResponses(
				(user?.metadata?.professionQuestions || professionQuestions).reduce(
					(acc, question) => ({
						...acc,
						[question.id]: question?.response,
					}),
					{},
				),
			);
		}
		return () => {};
	}, [professionQuestions, user]);

	useEffect(() => {
		const findProvince = provincesList.find(
			(item) => item.VALUE.toString().substr(0, 2) === postalCode.toString().substr(0, 2),
		);
		if (findProvince) {
			setProvince(findProvince.NAME);
			setCountry('España');
		} else {
			setProvince('');
			setCountry('');
		}
		return () => {};
	}, [postalCode]);

	// EFFECTS
	useEffect(() => {
		console.log('📌 user', user);
		console.log('📌 creationData', creationData);
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (simulationDataStep) {
			console.log('👉 simulationDataStep', simulationDataStep);
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [simulationDataStep]);
	return (
		<Wrapper showTopSection step={1} title={sectionTitle} subtitle={sectionSubtitle}>
			<View innerWrapperSt>
				{
					{
						1: (
							<>
								<BirthDate birthDate={birthDate} setBirthDate={setBirthDate} />

								<PostalCode
									postalCode={postalCode}
									setPostalCode={setPostalCode}
									isPostalCorrect={isPostalCorrect}
									setIsPostalCorrect={setIsPostalCorrect}
									setMunicipality={setMunicipality}
									postalCodeError={errorTowns}
								/>

								{loadingTowns && (
									<Loader type="regular" loadingText={loadingTowns} />
								)}

								{isPostalCorrect && municipalitiesList?.length > 0 && (
									<View inputFreeRowSt>
										<InputLabel label={'Población'} type="custom">
											<View
												rowSt
												justifyStartSt
												style={{
													gap: isMobile ? '20px' : '12px',
													flexWrap: 'wrap',
												}}
											>
												{municipalitiesList.map((item) => {
													return (
														<OptionButton
															key={item.value}
															width="auto"
															onClick={() =>
																setMunicipality(item.value)
															}
															selected={municipality === item.value}
															text={item.label}
															style={{
																width: isMobile
																	? '100%'
																	: undefined,
																margin: isMobile ? 0 : undefined,
															}}
														/>
													);
												})}
											</View>
										</InputLabel>
									</View>
								)}

								{/* {isDebug && (
									<Text errorSt smSt leftSt style={{ marginTop: 10 }}>
										👷‍♂️ Allianz WS: Os recomendamos encarecidamente que no
										emitáis pólizas con profesiones de riesgo (las que muestran
										preguntas) ya que estas generan un bloqueo que deben ser
										desbloqueadas a mano por nosotros, y hasta que no se
										desbloquee podréis indexar el DNI pero no la nota
										informativa por lo que fallará el proceso (después de la
										firma de los documentos).
									</Text>
								)} */}

								<Document
									dni={dni}
									setDni={setDni}
									isValidDNI={isValidDNI}
									setIsValidDNI={setIsValidDNI}
									invalidDNIreason={invalidDNIreason}
								/>
							</>
						),
						2: (
							<>
								<Profession
									professionCode={professionCode}
									setProfession={setProfession}
									setProfessionCode={setProfessionCode}
								/>

								{loadingQuestions && (
									<Loader type="regular" loadingText={loadingQuestions} />
								)}

								{!loadingQuestions && professionQuestions?.length > 0 && (
									<ProfessionQuestions
										professionQuestions={professionQuestions}
										professionResponses={professionResponses}
										setProfessionResponses={setProfessionResponses}
									/>
								)}

								{!loadingQuestions &&
									professionWarning &&
									professionWarning?.text !==
										'No existe cuestionario vinculado a la profesión' && (
										<Text errorSt smSt leftSt style={{ marginTop: 10 }}>
											{professionWarning?.text}
										</Text>
									)}
							</>
						),
					}[simulationDataStep]
				}
			</View>
			<BackNextButton
				isBackShown={simulationDataStep === 1 ? false : true}
				onClickBack={() => setSimulationDataStep(1)}
				onClickNext={next}
				isNextDisabled={isDisabled()}
			/>
		</Wrapper>
	);
};
